import {
  ErrorApiResponse,
  ID,
  ListApiResponse,
  SingleApiResponse,
  SuccessApiResponse
} from '@ianesthesie-kit/services';
import axios, { AxiosError } from 'axios';
import {
  CardiacStressTestProtocol,
  GenericPrescription,
  GenericPrescriptionPayload,
  GenericPrescriptionProtocol,
  LabOrderItem,
  LabOrderOption,
  PostopChronicPainProtocol,
  PreopChronicPainProtocol,
  Protocol,
  ProtocolItem,
  ProtocolItemOption,
  ProtocolItemOptionUnion,
  ProtocolMedicationOption,
  SurgeryPlanningProtocol,
  TelehealthProtocol
} from './protocol.model';
import {
  CombinedProtocolStatus,
  ProtocolName,
  ProtocolStatuses,
  ProtocolStatusesWithMeta,
  StatusTransitionMap
} from './protocol-status.model';
import { PatientId, WithPatientId } from '@/modules/surgicalEvent';
import { captureThrowOnDev } from '@/util';

export type ProtocolPostPayload<
  TStatus extends CombinedProtocolStatus = CombinedProtocolStatus,
  TItem extends { code: string } = { code: string }
> = {
  targetStatus: TStatus;
  items?: Partial<TItem>[];
  patientId: ID;
  protocol: ProtocolName;
  manualOverride?: boolean;
  printOnlyDoNotSend?: boolean;
};

export type ProtocolItemPayload = Required<
  Omit<
    ProtocolPostPayload,
    'targetStatus' | 'manualOverride' | 'printOnlyDoNotSend'
  >
>;

export class ProtocolService {
  private static readonly INSTANCE = new ProtocolService();
  private static readonly DEFAULT_GENERIC_MEDS_ORDER: ProtocolMedicationOption['code'][] =
    ['PARACETAMOL', 'KETOPROFENE', 'NEFOPAM', 'OPIUM_PARACETAMOL'];

  private readonly url;

  private constructor() {
    this.url = '/bff/doctor/patient';
  }

  public static getInstance() {
    return this.INSTANCE;
  }

  public static sortGenericPrescriptionOptions(
    a: ProtocolItemOption,
    b: ProtocolItemOption
  ): number {
    if (
      !ProtocolService.isMedicationOption(a) ||
      !ProtocolService.isMedicationOption(b)
    ) {
      captureThrowOnDev(
        new Error('ProtocolItemOption is not ProtocolMedicationOption')
      );
      return 0;
    }

    const indexA = ProtocolService.DEFAULT_GENERIC_MEDS_ORDER.indexOf(a.code);
    const indexB = ProtocolService.DEFAULT_GENERIC_MEDS_ORDER.indexOf(b.code);
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    if (indexA !== -1) {
      return -1;
    }

    if (indexB !== -1) {
      return 1;
    }

    return (a.nameWithDosage || a.name).localeCompare(
      b.nameWithDosage || b.name
    );
  }

  private static isMedicationOption(
    opt: ProtocolItemOption
  ): opt is ProtocolMedicationOption {
    return !!opt && 'nameWithDosage' in opt;
  }

  public getStatusTransitionMap(): Promise<StatusTransitionMap> {
    return this.fetchStatusTransitions()
      .then(({ data }) => data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public fetchProtocolItemOptionList(
    protocol?: ProtocolName
  ): Promise<ListApiResponse<ProtocolItemOptionUnion>> {
    let url = '/protocol/item/option';
    if (protocol) {
      let param = '';
      switch (protocol) {
        case ProtocolName.NICOTINE:
          param = 'NICOTINE';
          break;
        case ProtocolName.ANEMIA:
          param = 'ANEMIA';
          break;
        case ProtocolName.SLEEP_APNEA:
          param = 'SLEEP_APNEA';
          break;
        case ProtocolName.NUTRITION:
          param = 'NUTRITION';
          break;
        case ProtocolName.TELEHEALTH:
          param = 'TELEHEALTH';
          break;
        case ProtocolName.PREOP_LAB_TEST:
          param = 'PREOP_LAB_TEST';
          break;
        case ProtocolName.CARDIAC_STRESS_TEST:
          param = 'CARDIAC_STRESS_TEST';
          break;
        case ProtocolName.GENERIC_PRESCRIPTION:
          param = 'GENERIC_PRESCRIPTION';
          break;
        case ProtocolName.PREOP_CHRONIC_PAIN:
          param = 'PREOP_CHRONIC_PAIN';
          break;
        default:
          captureThrowOnDev(new Error(`Unhandled protocol type: ${protocol}`));
          return Promise.resolve({
            ok: true,
            count: 0,
            data: [] as ProtocolItemOptionUnion[]
          });
      }
      url += `?protocol-type=${param}`;
    }

    return axios
      .get<ListApiResponse<ProtocolItemOptionUnion>>(url)
      .then((r) => ({
        ...r.data,
        data:
          protocol === ProtocolName.PREOP_LAB_TEST
            ? r.data.data
            : r.data.data.sort(
                protocol === ProtocolName.GENERIC_PRESCRIPTION
                  ? ProtocolService.sortGenericPrescriptionOptions
                  : this.sortMedications
              )
      }))
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public fetchLabOrderPresetNames(): Promise<ListApiResponse<string>> {
    const url = `/protocol/${ProtocolName.PREOP_LAB_TEST}/lab-order-preset`;

    return axios
      .get<ListApiResponse<string>>(url)
      .then((r) => r.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  /* eslint-disable prettier/prettier */
  public fetchStatuses(
    patientId: ID
  ): Promise<SingleApiResponse<ProtocolStatuses>>;
  public fetchStatuses(
    patientId: ID,
    includeStatusMeta: false | undefined
  ): Promise<SingleApiResponse<ProtocolStatuses>>;
  public fetchStatuses(
    patientId: ID,
    includeStatusMeta: true
  ): Promise<SingleApiResponse<ProtocolStatusesWithMeta>>;
  public fetchStatuses(
    patientId: ID,
    includeStatusMeta?: boolean
  ): Promise<SingleApiResponse<ProtocolStatusesWithMeta | ProtocolStatuses>>;
  public fetchStatuses(
    patientId: ID,
    includeStatusMeta?: boolean
  ): Promise<SingleApiResponse<ProtocolStatusesWithMeta | ProtocolStatuses>> {
    /* eslint-enable prettier/prettier */
    if (!patientId) {
      throw new Error('No patient id');
    }
    if (includeStatusMeta) {
      return axios
        .get<SingleApiResponse<ProtocolStatusesWithMeta>>(
          this.endpoint('/status?meta=true', patientId)
        )
        .then((r) => r.data)
        .catch((err: AxiosError<ErrorApiResponse>) => {
          throw err.response?.data?.error || JSON.stringify(err);
        });
    }
    return axios
      .get<SingleApiResponse<ProtocolStatuses>>(
        this.endpoint('/status', patientId)
      )
      .then((r) => r.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public fetchProtocol(
    protocolName: ProtocolName.GENERIC_PRESCRIPTION,
    patientId: ID
  ): Promise<SingleApiResponse<GenericPrescriptionProtocol>>;
  public fetchProtocol(
    protocolName: ProtocolName.CARDIAC_STRESS_TEST,
    patientId: ID
  ): Promise<SingleApiResponse<CardiacStressTestProtocol>>;
  public fetchProtocol(
    protocolName: ProtocolName.TELEHEALTH,
    patientId: ID
  ): Promise<SingleApiResponse<TelehealthProtocol>>;
  public fetchProtocol(
    protocolName: ProtocolName.SURGERY_PLANNING,
    patientId: ID
  ): Promise<SingleApiResponse<SurgeryPlanningProtocol>>;
  public fetchProtocol(
    protocolName: ProtocolName.PREOP_CHRONIC_PAIN,
    patientId: ID
  ): Promise<SingleApiResponse<PreopChronicPainProtocol>>;
  public fetchProtocol(
    protocolName: ProtocolName.POSTOP_CHRONIC_PAIN,
    patientId: ID
  ): Promise<SingleApiResponse<PostopChronicPainProtocol>>;
  public fetchProtocol(
    protocolName: Omit<
      ProtocolName,
      | ProtocolName.GENERIC_PRESCRIPTION
      | ProtocolName.CARDIAC_STRESS_TEST
      | ProtocolName.TELEHEALTH
      | ProtocolName.SURGERY_PLANNING
    >,
    patientId: ID
  ): Promise<SingleApiResponse<Protocol>>;
  public fetchProtocol(
    protocolName: ProtocolName,
    patientId: ID
  ): Promise<SingleApiResponse<Protocol>> {
    const path = `${this.endpoint(protocolName, patientId)}?with-items=true`;
    return axios
      .get<SingleApiResponse<Protocol>>(path)
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public addProtocolItems({
    patientId,
    protocol,
    items
  }: ProtocolItemPayload): Promise<ListApiResponse<ProtocolItem>> {
    if (!patientId) {
      throw new Error('No patient id');
    }
    if (!protocol) {
      throw new Error('No protocol name');
    }
    if (protocol === ProtocolName.PREOP_LAB_TEST) {
      throw new Error('Use addLabOrderItems for Preop Lab Test protocol');
    }
    if (!items?.length) {
      throw new Error('No items provided');
    }
    const reqBody = items.map((it) => it.code);
    return axios
      .post<ListApiResponse<ProtocolItem>>(
        this.endpoint(`/${protocol}/item`, patientId),
        reqBody
      )
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public addLabOrderItems({
    patientId,
    category,
    items
  }: Omit<ProtocolItemPayload, 'protocol'> & {
    category: LabOrderOption['category'];
  }): Promise<ListApiResponse<LabOrderItem>> {
    if (!patientId) {
      throw new Error('No patient id');
    }
    if (!items?.length) {
      throw new Error('No items provided');
    }
    const reqBody = items.map((it) => ({
      code: it.code,
      category
    }));
    return axios
      .post<ListApiResponse<ProtocolItem>>(
        this.endpoint(`/${ProtocolName.PREOP_LAB_TEST}/item`, patientId),
        reqBody
      )
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public setLabOrderItemsFromPreset({
    patientId,
    preset
  }: WithPatientId<{ preset: string }>): Promise<
    ListApiResponse<ProtocolItem>
  > {
    if (!patientId) {
      throw new Error('No patient id');
    }
    if (!preset) {
      throw new Error('No preset');
    }

    const url = this.endpoint(
      `/${ProtocolName.PREOP_LAB_TEST}/item`,
      patientId
    ).concat(`?preset=${preset}`);

    return axios
      .post<ListApiResponse<ProtocolItem>>(url)
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public removeProtocolItems({
    patientId,
    protocol,
    items
  }: ProtocolItemPayload): Promise<ListApiResponse<ProtocolItem>> {
    if (!patientId) {
      throw new Error('No patient id');
    }
    if (!protocol) {
      throw new Error('No protocol name');
    }
    if (!items?.length) {
      throw new Error('No items provided');
    }
    const reqBody = items.map((it) => it.code);
    return axios
      .delete<ListApiResponse<ProtocolItem>>(
        this.endpoint(`/${protocol}/item`, patientId),
        {
          data: reqBody
        }
      )
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public updateProtocol({
    patientId,
    protocol,
    targetStatus,
    manualOverride,
    printOnlyDoNotSend,
    ...payload
  }: ProtocolPostPayload): Promise<SingleApiResponse<Protocol>> {
    if (!patientId) {
      throw new Error('No patient id');
    }
    if (!protocol) {
      throw new Error('No protocol name');
    }
    let qs = '';
    if (manualOverride || printOnlyDoNotSend) {
      qs = '?';
      if (manualOverride) {
        qs += 'manual-override=true';
      }
      if (printOnlyDoNotSend) {
        if (manualOverride) {
          qs += '&';
        }
        qs += 'print-only=true';
      }
    }

    return axios
      .post<SingleApiResponse<Protocol>>(
        this.endpoint(`/${protocol}${qs}`, patientId),
        { ...payload, status: targetStatus }
      )
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public toggleAutoQuestionnaireAssignementsSuspended({
    protocol,
    patientId,
    suspend
  }: {
    protocol: ProtocolName;
    patientId: string;
    suspend?: boolean;
  }): Promise<SuccessApiResponse> {
    if (!patientId) {
      throw new Error('No patient id');
    }

    let param;
    if (suspend === undefined) {
      param = '';
    } else if (suspend) {
      param = '?suspend=true';
    } else {
      param = '?suspend=false';
    }

    return axios
      .put<SuccessApiResponse>(
        this.endpoint(`/${protocol}/questionnaire/suspend${param}`, patientId)
      )
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public assignEarlyPostopQuestionnaire({
    patientId,
    sendNotification
  }: {
    patientId: string;
    sendNotification?: boolean;
  }): Promise<SuccessApiResponse> {
    if (!patientId) {
      throw new Error('No patient id');
    }

    const param = `?notification=${sendNotification ? 'true' : 'false'}`;

    return axios
      .post<SuccessApiResponse>(
        this.endpoint(
          `/${ProtocolName.EARLY_POSTOP}/questionnaire${param}`,
          patientId
        )
      )
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public sendInformedConsent({
    patientId,
    updateStatus,
    ...payload
  }: { updateStatus: boolean } & Partial<
    Omit<ProtocolPostPayload, 'protocol' | 'targetStatus'>
  >): Promise<SuccessApiResponse> {
    if (!patientId) {
      throw new Error('No patient id');
    }
    const param = updateStatus ? '?update-status=true' : '';
    return axios
      .post<SuccessApiResponse>(
        this.endpoint(
          `/${ProtocolName.TELEHEALTH}/send-consent${param}`,
          patientId
        ),
        { ...payload }
      )
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public triggerAnemiaCopilot({
    patientId
  }: PatientId): Promise<SuccessApiResponse> {
    if (!patientId) {
      throw new Error('No patient id');
    }

    return axios
      .post<SuccessApiResponse>(
        this.endpoint(`/${ProtocolName.ANEMIA}/co-pilot`, patientId)
      )
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public upsertPrescription({
    patientId,
    ...payload
  }: WithPatientId<GenericPrescriptionPayload>): Promise<
    SingleApiResponse<GenericPrescription>
  > {
    if (!patientId) {
      throw new Error('No patient id');
    }

    return axios
      .post<SingleApiResponse<GenericPrescription>>(
        this.endpoint(`/${ProtocolName.GENERIC_PRESCRIPTION}/item`, patientId),
        { ...payload }
      )
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public sendPrescription({
    patientId,
    printOnlyDoNotSend,
    ...payload
  }: WithPatientId<GenericPrescriptionPayload>): Promise<
    SingleApiResponse<GenericPrescription>
  > {
    if (!patientId) {
      throw new Error('No patient id');
    }

    if (!payload?.drugs.length) {
      throw new Error('Drug list cannot be empty');
    }

    const qs = printOnlyDoNotSend ? '?print-only=true' : '';

    return axios
      .post<SingleApiResponse<GenericPrescription>>(
        this.endpoint(
          `/${ProtocolName.GENERIC_PRESCRIPTION}/item/send${qs}`,
          patientId
        ),
        { ...payload }
      )
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public deletePrescription({
    patientId,
    ...prescription
  }: WithPatientId<GenericPrescription>): Promise<SuccessApiResponse> {
    if (!patientId) {
      throw new Error('No patient id');
    }

    if (!prescription.deletable) {
      throw new Error(`Prescription ${prescription.id} cannot be deleted`);
    }

    return axios
      .delete<SuccessApiResponse>(
        this.endpoint(
          `/${ProtocolName.GENERIC_PRESCRIPTION}/item/${prescription.id}`,
          patientId
        )
      )
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public updateProtocolComments({
    patientId,
    protocol,
    value
  }: WithPatientId<{
    value: string;
    protocol: ProtocolName;
  }>): Promise<SuccessApiResponse> {
    if (!patientId) {
      throw new Error('No patient id');
    }

    if (!protocol) {
      throw new Error('No protocol name');
    }

    const text = value || null;

    return axios
      .put<SuccessApiResponse>(
        this.endpoint(`/${protocol}/comments`, patientId),
        { text }
      )
      .then((resp) => resp.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  private endpoint(segm: string, patientId: ID): string {
    return `${this.url}/${patientId}/surgical-event/active/protocol/${segm}`.replaceAll(
      '//',
      '/'
    );
  }

  private sortMedications(
    { code: name1 }: ProtocolItemOption,
    { code: name2 }: ProtocolItemOption
  ): number {
    const med1ContainsNum = /\d/.test(name1);
    const med2ContainsNum = /\d/.test(name2);

    if (med1ContainsNum && med2ContainsNum) {
      const [, med1, mg1] = name1.match(/(.+)\s(\d+)/) ?? [];
      const [, med2, mg2] = name2.match(/(.+)\s(\d+)/) ?? [];

      if (med1 === med2) {
        return +mg1 < +mg2 ? -1 : 1;
      }
    }
    return name1.localeCompare(name2);
  }

  private fetchStatusTransitions(): Promise<
    SingleApiResponse<StatusTransitionMap>
  > {
    return axios
      .get('/protocol/status/available')
      .then((resp) => resp.data)
      .catch((err) => {
        // TODO retry
        console.log(err);
      });
  }
}
